import React, { useEffect, useCallback, useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import http from '../../httpConfig';
import Toast from '../../components/Toast';
import AuthService from '../../services/auth.service';
import { ERROR_MESSAGES, STATUS_CODE } from '../../utils/apiConstant';
import { AuthContext } from '../../contexts/AuthContext';
import { Modules } from '../../enums';

const HttpInterceptor: React.FC = () => {
  const navigate = useNavigate();
  const { logout, setException } = useContext(AuthContext);

  const handleError = useCallback(async (error: any) => {
    if (error?.response?.status === STATUS_CODE.unauthorized) {
      toast.error(
        <Toast message={error?.response?.data?.message ?? ERROR_MESSAGES.unauthorized} />,
        { icon: false, toastId: 'unauthorized' }
      );
      await logout(error?.config?.url);
      navigate(Modules.auth);
    } else if (error?.response?.status === STATUS_CODE.notFound) {
      toast.error(
        <Toast message={error?.response?.data?.message ?? ERROR_MESSAGES.apiPathNotFound} />,
        { icon: false, toastId: 'not-found' }
      );
      return Promise.reject({
        success: false,
        message: error?.response?.data?.message ?? ERROR_MESSAGES.apiPathNotFound,
        code: error?.response?.data?.code,
      });
    } else if (error?.response?.status === STATUS_CODE.gatewayTimeout) {
      toast.error(<Toast message={ERROR_MESSAGES.gatewayTimeout} />, {
        icon: false,
        toastId: 'gateway-timeout',
      });
      return Promise.reject({ success: false });
    } else if (error?.response?.status === STATUS_CODE.badRequest) {
      toast.error(<Toast message={error?.response?.data?.message ?? ERROR_MESSAGES.badRequest} />, {
        icon: false,
        toastId: 'bad-request',
      });
      return Promise.reject({ success: false, message: ERROR_MESSAGES.badRequest });
    } else if (error?.response?.status === STATUS_CODE.unProcessableEntity) {
      const messages: any[] = [];
      Object.keys(error?.response?.data?.errors).forEach((errorField) =>
        messages.push(error?.response?.data?.errors[errorField][0])
      );
      toast.error(<Toast message={messages} />, { icon: false, toastId: 'bad-request' });
      return Promise.reject({ success: false, message: ERROR_MESSAGES.badRequest });
    } else if (error?.response?.status === STATUS_CODE.conflict) {
      toast.error(<Toast message={error?.response?.data?.message ?? ERROR_MESSAGES.badRequest} />, {
        icon: false,
        toastId: 'conflict',
      });
      return Promise.reject({ success: false, message: ERROR_MESSAGES.badRequest });
    } else if (error?.response?.status === STATUS_CODE.forbidden) {
      toast.error(<Toast message={error?.response?.data?.message ?? ERROR_MESSAGES.forbidden} />, {
        icon: false,
        toastId: 'forbidden',
      });
      return Promise.reject({ success: false, message: ERROR_MESSAGES.forbidden });
    }

    // if (error?.response?.status === STATUS_CODE.entityTooLarge) {
    //   return Promise.reject({ success: false, code: STATUS_CODE.entityTooLarge });
    // }
    else if (error?.response?.status === STATUS_CODE.internalServerError) {
      if (error?.response?.data?.code && error?.response?.data?.code === 1000) {
        setException(error?.response?.data?.data?.correlation_id ?? 'test');
      } else {
        toast.error(
          <Toast message={error?.response?.data?.message ?? ERROR_MESSAGES.internalServerError} />,
          { icon: false, toastId: 'unknown' }
        );
      }

      return Promise.reject({ success: false, message: ERROR_MESSAGES.internalServerError });
    } else {
      toast.error(
        <Toast message={error?.response?.data?.message ?? ERROR_MESSAGES.internalServerError} />,
        { icon: false, toastId: 'unknown' }
      );
      return Promise.reject({ success: false, message: ERROR_MESSAGES.internalServerError });
    }
    // return Promise.reject({success: false, ...error?.response?.data});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [logout, navigate, setException]);

  useEffect(() => {
    // Request interceptor
    const reqInterceptor = http.interceptors.request.use(
      (config: any) => {
        const token = AuthService.getToken();
        if (token) {
          config.headers = config.headers || {};
          config.headers.Authorization = `Bearer ${token}`;
        }
        return config;
      },
      (error: any) => {
        return Promise.reject(error);
      }
    );

    // Response interceptor
    const resInterceptor = http.interceptors.response.use(
      (response: any) => {
        return response?.data?.data ?? response?.data ?? {};
      },
      (error: any) => {
        return handleError(error);
      }
    );

    return () => {
      http.interceptors.request.eject(reqInterceptor);
      http.interceptors.response.eject(resInterceptor);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [handleError]);

  return null;
};

export default HttpInterceptor;
